import { getApiBaseUrl } from './config';
import { apiFetch } from './client';

const API_BASE_URL = () => `${getApiBaseUrl()}/admin`;

// --- Approval Workflows ---

/**
 * Fetches all pending survey approval requests.
 * @returns {Promise<Array>} A list of pending requests.
 */
export const getPendingApprovalRequests = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/approvals`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to fetch pending approval requests.");
  }
  return res.json();
};

/**
 * Sends a request to approve or deny a survey.
 * @param {number} requestId - The ID of the request to process.
 * @param {string} action - 'approve' or 'deny'.
 * @param {number} adminId - The ID of the logged-in administrator.
 * @param {string} feedback - Feedback required for denial, optional for approval.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const handleApprovalAction = async (requestId, action, adminId, feedback) => {
  const res = await apiFetch(`${API_BASE_URL()}/approval/${requestId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      action: action,
      admin_id: adminId,
      feedback: feedback,
    }),
  });

  const data = await res.json();
  
  if (!res.ok) {
    throw new Error(data.error || `Failed to ${action} request ${requestId}.`);
  }
  return data;
};

// --- System Settings (Semester Dates) ---

/**
 * Fetches current system settings (e.g. semester dates).
 */
export const getSystemSettings = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/settings`);
  if (!res.ok) throw new Error("Failed to fetch settings.");
  return res.json();
};

/**
 * Updates system settings.
 * @param {object} settings - e.g. { semester_1_deployment_date: "2025-12-01" }
 */
export const updateSystemSettings = async (settings) => {
  const res = await apiFetch(`${API_BASE_URL()}/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings)
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || "Failed to update settings.");
  }
  return res.json();
};

/**
 * Update the logged-in admin email address.
 * @param {string} email
 */
export const updateAdminEmail = async (email) => {
  const res = await apiFetch(`${API_BASE_URL()}/account/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update email.');
  }
  return data;
};

/**
 * Update the logged-in admin password.
 * @param {string} password
 */
export const updateAdminPassword = async (password) => {
  const res = await apiFetch(`${API_BASE_URL()}/account/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update password.');
  }
  return data;
};

// --- Module Data Management ---

/**
 * Imports a list of modules from a CSV file (ModuleCode, ModuleName, LeaderEmail, Semester).
 * @param {File} fileObject - The file object from the file input.
 */
export const importModulesCsv = async (fileObject) => {
  const formData = new FormData();
  formData.append('file', fileObject);

  const res = await apiFetch(`${API_BASE_URL()}/modules/import`, {
    method: 'POST',
    body: formData
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to import modules.");
  return data;
};

/**
 * Fetches all modules in the system.
 * @returns {Promise<Array>} List of modules including leader details.
 */
export const getAllModulesForAdmin = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/modules`);
  if (!res.ok) throw new Error("Failed to fetch all modules.");
  return res.json();
};

/**
 * Fetches approved requests that are pending deployment (no survey created yet).
 */
export const getPendingDeployments = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/pending_deployments`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch pending deployments');
  }
  return res.json();
};

/**
 * Fetch approved requests and modules with enrollments for a specific academic year.
 * @param {number} year - optional academic year to view (start year, e.g., 2026)
 */
export const getPendingDeploymentsForYear = async (year) => {
  let url = `${API_BASE_URL()}/pending_deployments`;
  if (year) url += `?year=${year}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch pending deployments');
  }
  return res.json();
};

/**
 * Fetch academic years that currently have pending deployments.
 * @returns {Promise<number[]>}
 */
export const getPendingDeploymentYears = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/pending_deployment_years`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch pending deployment years');
  }
  const data = await res.json();
  return data.years || [];
};

/**
 * Deploys a pending approval request by creating the survey and enrolling students.
 * @param {number} requestId
 * @param {number} adminId
 */
export const deployApprovalRequest = async (requestId, adminId, year) => {
  const body = { admin_id: adminId };
  if (year) body.year = year;
  const res = await apiFetch(`${API_BASE_URL()}/deploy_request/${requestId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Failed to deploy request ${requestId}`);
  return data;
};

/**
 * Deploy a module directly without an approval request.
 * @param {number} moduleId
 * @param {number} adminId
 */
export const deployModule = async (moduleId, adminId, year) => {
  const body = { admin_id: adminId };
  if (year) body.year = year;
  const res = await apiFetch(`${API_BASE_URL()}/deploy_module/${moduleId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Failed to deploy module ${moduleId}`);
  return data;
};

/**
 * Fetch surveys that have been deployed.
 */
export const getDeployedSurveys = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/deployed_surveys`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch deployed surveys');
  }
  return res.json();
};

/**
 * Fetch tokens for a deployed survey (includes full access URL).
 * @param {number} surveyId
 */
export const getSurveyTokens = async (surveyId) => {
  const res = await apiFetch(`${API_BASE_URL()}/survey_tokens/${surveyId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch survey tokens');
  }
  return res.json();
};

/**
 * Generate and send survey links for a deployed survey.
 * @param {number} surveyId
 * @param {boolean} force - whether to regenerate and resend existing tokens
 */
export const sendSurvey = async (surveyId, force = false) => {
  const res = await apiFetch(`${API_BASE_URL()}/send_survey/${surveyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ force })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to send survey emails');
  return data;
};

/**
 * Updates the details (leader, semester) for a specific module.
 * NOTE: Assuming a new endpoint for module updates: /api/admin/modules/<id>
 * @param {number} moduleId
 * @param {object} updatedDetails - { module_leader_id: number, semester: string }
 */
export const updateModuleDetails = async (moduleId, updatedDetails) => {
  const res = await apiFetch(`${API_BASE_URL()}/modules/${moduleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updatedDetails)
  });
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Failed to update module ${moduleId}.`);
  return data;
};


/**
 * Uploads a student enrollment list (Register) for a specific module.
 * Expected CSV headers: StudentID, Email
 * @param {number} moduleId - The ID of the module to enroll students in.
 * @param {File} fileObject - The CSV file.
 */
export const uploadModuleEnrollment = async (moduleId, fileObject) => {
  const formData = new FormData();
  formData.append('file', fileObject);

  const res = await apiFetch(`${API_BASE_URL()}/modules/${moduleId}/enrollment`, {
    method: 'POST',
    body: formData
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to upload enrollment.");
  return data;
};

/**
 * Upload enrollment CSV for a module for a specific academic year.
 * @param {number} moduleId
 * @param {File} fileObject
 * @param {number} year - academic year start (e.g. 2025)
 */
export const uploadModuleEnrollmentForYear = async (moduleId, fileObject, year) => {
  const formData = new FormData();
  formData.append('file', fileObject);
  if (year) formData.append('year', String(year));

  const res = await apiFetch(`${API_BASE_URL()}/modules/${moduleId}/enrollment`, {
    method: 'POST',
    body: formData
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to upload enrollment.');
  return data;
};

/**
 * Replaces module enrollment rows for a given academic year using a manually edited list.
 * @param {number} moduleId
 * @param {number} year
 * @param {Array<{student_email: string, student_identifier?: string}>} students
 */
export const saveModuleEnrollmentForYear = async (moduleId, year, students) => {
  const res = await apiFetch(`${API_BASE_URL()}/modules/${moduleId}/enrollment/manual`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ year, students })
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to save roster.');
  return data;
};

/**
 * Fetches all accounts with the `Leader` role for dropdowns and selection UIs.
 * @returns {Promise<Array>} List of leader accounts: { account_id, email, first_name, last_name }
 */
export const getModuleLeaders = async () => {
  const res = await apiFetch(`${API_BASE_URL()}/leaders`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch module leaders.');
  }
  return res.json();
};

/**
 * Fetch module enrollments (diagnostic)
 * @param {number} moduleId
 */
export const getModuleEnrollments = async (moduleId, year) => {
  let url = `${API_BASE_URL()}/module_enrollments/${moduleId}`;
  if (year) url += `?year=${year}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch module enrollments');
  }
  return res.json();
};

/**
 * Creates a new leader account. Returns the generated password in the response.
 * @param {{email: string, first_name?: string, last_name?: string}} payload
 */
export const createLeaderAccount = async (payload) => {
  const res = await apiFetch(`${API_BASE_URL()}/leaders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || data?.message || 'Failed to create leader');
  
  return data;
};